import React, { useState, useEffect, useRef, useMemo } from "react";
import { Search, Menu } from "lucide-react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { getOrCreateSessionId } from "../utils/session";
import BagLoader from "../components/BagLoader";
import { getCachedProducts, setCachedProducts } from "../store/cache";


const CATEGORIES = ["t-shirts", "shirts", "shoes", "sunglasses", "belts", "trousers", "jeans"];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

    // 👇 ADD THIS
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);
  // 👆 UP TO HERE

  const instanceRef = useRef(null);
  const prevSlideRef = useRef(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [loadedSlides, setLoadedSlides] = useState([0]); // first slide is loaded by default
  const [isLoading, setIsLoading] = useState(true);
  const hasSetInitialSlide = useRef(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);
const [customProducts, setCustomProducts] = useState(getCachedProducts() || []);

  





  const navigate = useNavigate();

  const [sliderRef] = useKeenSlider(
    {
      loop: true, // <-- important
      slides: {
        perView: 1,
        spacing: 0,
      },
      slideChanged(slider) {
        const newIndex = slider.track.details.rel;
        setCurrentSlide(newIndex);

        const lastIndex = heroSlides.length - 1;



        prevSlideRef.current = newIndex;
      }


,
      created(slider) {
        instanceRef.current = slider;
      },
    },
    []
  );



const heroSlides = useMemo(() => {
  const slides = customProducts
    .map((product) => {
      // ✅ Case 1: imageUrl stored as array of objects (new format)
      if (Array.isArray(product.imageUrl) && product.imageUrl.length > 0) {
        const first = product.imageUrl[0]; // preserves Excel order
        const image = first.urls?.[0];
        return image
          ? { image, name: product.name || "Unnamed Product", id: product.id, type: product.type }
          : null;
      }

      // ✅ Case 2: imageUrl stored as map/dict (old format)
      if (product.imageUrl && typeof product.imageUrl === "object") {
        const firstColor = Object.keys(product.imageUrl)[0];
        const image = product.imageUrl[firstColor]?.[0];
        return image
          ? { image, name: product.name || "Unnamed Product", id: product.id, type: product.type }
          : null;
      }

      // ✅ Fallback to imageUrls array
      if (Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
        return {
          image: product.imageUrls[0],
          name: product.name || "Unnamed Product",
          id: product.id,
          type: product.type
        };
      }

      return null;
    })
    .filter(Boolean);


  return slides;
}, [customProducts]);




const fetchCustomProducts = async (slideIndex = 0) => {
  try {
    const sessionId = getOrCreateSessionId(); // ✅ get session ID
    const response = await api.get('/cproducts', {
      params: { 
        session_id: sessionId,
        slide_index: slideIndex, // <-- Pass current slide index
      },
    });
    const products = response.data.cproducts || [];
    setCustomProducts(products);
    setCachedProducts(products); // <-- save to cache
    setIsLoading(false);
  } catch (error) {
    console.error("Failed to fetch custom products:", error);
  }
};


useEffect(() => {
  if (!instanceRef.current || heroSlides.length === 0) return;

  // Only **preload** next folder when on the last slide of current folder
  // But do NOT rotate the folder yet
  const slidesPerFolder = 3;
  const isLastSlideOfFolder = (currentSlide + 1) % slidesPerFolder === 0;

  if (isLastSlideOfFolder) {
    // Preload next folder
    fetchCustomProducts(currentSlide + 1);
  }
}, [currentSlide]);





useEffect(() => {
  if (customProducts.length === 0) {
    fetchCustomProducts();
  }
}, []);




useEffect(() => {
  if (instanceRef.current && customProducts.length > 0) {
    // Get last viewed slide from sessionStorage, fallback to 0
    const lastSlide = sessionStorage.getItem("lastSlide") || 0;
    instanceRef.current.moveToIdx(Number(lastSlide));
    setCurrentSlide(Number(lastSlide));
  }
}, [customProducts]);

useEffect(() => {
  sessionStorage.setItem("lastSlide", currentSlide);
}, [currentSlide]);




// ✅ NEW: Preload the first image (slide 0) only, and show the slider immediately after.
  useEffect(() => {
    if (heroSlides.length === 0 || firstImageLoaded) return;

    const img = new Image();
    img.src = heroSlides[0].image;

    img.onload = () => {
      setFirstImageLoaded(true); // <--- Critical: Allows slider to render!
      setLoadedSlides([0]);      // Mark the first slide as loaded.
      
      // Remove the overall 'loading' screen state
      setImagesLoaded(true);
    };
    
    img.onerror = () => {
      // Fallback: still show the slider even if first image fails to load
      setFirstImageLoaded(true);
      setImagesLoaded(true);
      setLoadedSlides([0]);
    };
  }, [heroSlides, firstImageLoaded]);



  useEffect(() => {
    if (!loadedSlides.includes(currentSlide)) {
      setLoadedSlides((prev) => [...prev, currentSlide]);
    }
    
  }, [currentSlide]);


    // ✅ Refactored: Preload the immediate neighbors (next and previous) on slide change
  useEffect(() => {
    if (heroSlides.length === 0 || !firstImageLoaded) return; // Wait for first image

    const next = (currentSlide + 1) % heroSlides.length;
    const prev = (currentSlide - 1 + heroSlides.length) % heroSlides.length;

    // Only attempt to load images that aren't already loaded
    const slidesToLoad = [next, prev].filter(idx => !loadedSlides.includes(idx));

    slidesToLoad.forEach((idx) => {
      const img = new Image();
      img.src = heroSlides[idx].image;
      img.onload = () => {
        setLoadedSlides((prev) => [...new Set([...prev, idx])]); // Update state with loaded index
      };
    });
  }, [currentSlide, heroSlides, loadedSlides, firstImageLoaded]);




const handleSearch = async (query = searchTerm) => {
  if (!query.trim()) return;
  setIsSearching(true);
  setHasSearched(true);
  setShowSuggestions(false);
  setLastQuery(query);

  try {
    const sessionId = getOrCreateSessionId();
    const response = await api.get('/search', {
      params: { q: query, session_id: sessionId },
    });

    // 🔹 Show only the latest 4 results instead of appending
    setSearchResults((response.data.products || []).slice(0, 4));
    window.scrollTo({ top: 0, behavior: "smooth" }); // 🔹 Scroll to top
    setFeedback(response.data.message || "");
  } catch (err) {
    console.error("Search error:", err);
    setFeedback("Something went wrong. Try again later.");
  } finally {
    setIsSearching(false);
  }
};


  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const getFirstImage = (imageObj) => {
    if (imageObj && typeof imageObj === "object") {
      const firstColor = Object.keys(imageObj)[0];
      return imageObj[firstColor]?.[0] || "";
    }
    return "";
  };



  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-black flex flex-col items-center px-2 pb-20">
      <div className="w-full max-w-3xl mx-auto">
      {/* Header */}
      <header  className="mt-8 w-full py-3">
        <div className="w-full flex items-center justify-between px-4">
          {/* Left placeholder */}
          <div className="w-6 h-6 mr-4">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Logo */}
          <div className="flex-1 flex justify-center">
            <img src="/A2XSHOP_.webp" alt="A2XSHOP.COM" className="h-4 w-auto object-contain" />
          </div>

        </div>

        {/* Mobile dropdown menu */}
        {isMenuOpen && (
          <div 
            ref={menuRef}   // 👈 add this
            className="absolute top-16 left-0 right-0 bg-white border-t border-gray-200 shadow-md z-20">
            <button
              className="w-full text-left px-4 py-3 border-b border-gray-200"
              onClick={() => {
                setIsMenuOpen(false);
                navigate("/policies");
              }}
            >
              Policies & Support
            </button>
          </div>
        )}
      </header>




        {/* Hero + Actions */}
        {searchResults.length === 0 && (
          <>
            <div className="w-full mt-6">
              <div className="relative w-full md:max-w-[400px] md:h-[600px] aspect-[3/5] mx-auto">
                {/* Bag Loader */}
                {!firstImageLoaded  && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
                    <div className="w-72 h-80 flex items-center justify-center">
                      <BagLoader />
                    </div>
                    <p className="mt-4 text-gray-400 text-sm font-sans uppercase tracking-widest text-center">
                      Loading...
                    </p>
                  </div>
                )}


                {/* Slider */}
                {firstImageLoaded && ( /* <--- CHANGED FROM imagesLoaded */
                  <div
                    ref={sliderRef}
                    className="keen-slider rounded-xl overflow-hidden h-full"
                  >
                    {heroSlides.map((slide, idx) => (
                    <div
                      key={idx}
                      className="keen-slider__slide cursor-pointer flex flex-col items-center"
                     onClick={() => navigate(`/customize/${heroSlides[currentSlide].id}`)}

                    >
                      {loadedSlides.includes(idx) ? (
                        <img
                          src={slide.image}
                          alt={`Hero ${idx + 1}`}
                          className="w-full h-[100%] object-cover rounded-md transition-opacity duration-500"
                          loading={idx === 0 ? "eager" : "lazy"}
                          width={500}
                          height={600}
                        />
                      ) : (
                        <div className="w-full h-[85%] flex items-center justify-center bg-gray-100">
                          Loading...
                        </div>
                      )}

                      {/* ✅ Slide title centered */}
                      <h2 className="text-lg md:text-xl font-bold text-center text-gray-800 mt-4 mb-2 w-full">
                        {slide.name}
                      </h2>
                    </div>

                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Dots outside slider */}
            <div className="flex justify-center gap-2 mt-4 mb-8">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => instanceRef.current?.moveToIdx(i)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === i ? "bg-black" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            {/* CTA Buttons for current slide */}
            {/* CTA Buttons for current slide */}
            {/* CTA Buttons for current slide */}
            {heroSlides.length > 0 && (
              <div className="flex justify-center mt-6 mb-6">
                <button
                  onClick={() => navigate(`/customize/${heroSlides[currentSlide].id}`)}
                  className="px-6 py-2 bg-black text-white rounded-full shadow-md hover:bg-gray-900 transition"
                >
                  Buy Now
                </button>
              </div>
            )}


          </>
        )}


      {/* Search Results */}
      {/* Search Results */}
      {searchResults.length > 0 && (
        <>
          <div className="mt-10 w-full grid grid-cols-1 sm:grid-cols-2 gap-6 px-4">
            {searchResults.map((product) => (
              <a
                href={product.platform1_url}
                key={product.id}
                target="_blank"
                rel="noopener noreferrer"
                className="border rounded-xl p-4 hover:shadow-md transition relative block"
              >
                <div className="relative w-full aspect-[3/4] md:min-h-[350px] overflow-hidden rounded-md">
                  <img
                    src={getFirstImage(product.imageUrl)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />

                  {/* ✅ Replaced inner <a> with a <button> to avoid nested anchors */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(product.platform1_url, "_blank", "noopener,noreferrer");
                    }}
                    className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-white/90 hover:bg-white shadow-sm transition"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-3.5 h-3.5 text-gray-700"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </button>
                </div>

                <h3 className="mt-3 text-sm font-semibold">{product.description}</h3>
                <p className="text-xs text-gray-600">{product.platform1_name}</p>
              </a>
            ))}
          </div>

          {/* Show More Button */}
          {!feedback.toLowerCase().includes("no more") && (
            <div className="text-center mt-6">
              <button
                onClick={() => handleSearch(lastQuery)}
                className="px-5 py-2 bg-black text-white rounded-full hover:bg-gray-900 transition"
              >
                Show More
              </button>
            </div>
          )}

          {/* 🏠 Home button */}
          <div className="flex justify-center mt-6">
            <button
              onClick={() => {
                setSearchResults([]);
                setSearchTerm("");
                setHasSearched(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full border border-black hover:bg-gray-100 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
                className="w-5 h-5 text-black"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 9.75L12 3l9 6.75M4.5 10.5V21h15V10.5"
                />
              </svg>
            </button>
          </div>
        </>
      )}




        {/* Footer */}
        {/* Fixed Footer Search Bar (always visible) */}
<footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-white shadow-md z-50">
  <div className="max-w-3xl mx-auto px-4 py-1">
    <div className="relative">
      <input
        type="text"
        placeholder="search across shops"
        className="w-full px-5 py-3 pr-12 border border-gray-200 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-black transition text-sm md:text-base"
        value={isSearching ? "Searching..." : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setSearchTerm("");
          setSearchResults([]);
          setFeedback("");
          setLastQuery("");
          setHasSearched(false);
          setShowSuggestions(true);
        }}
        onBlur={() => {
          setTimeout(() => setShowSuggestions(false), 150);
        }}
      />
      <Search
        onClick={() => handleSearch()}
        className="w-5 h-5 absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
      />
    </div>

    {/* Suggestions — open above the footer search bar */}
    {showSuggestions && searchTerm.trim() === "" && (
      <div className="absolute z-30 left-0 right-0 bottom-full mb-2 bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 w-full">
        <div className="flex flex-wrap justify-center gap-2">
          {CATEGORIES.map((category) => (
            <button
              key={category}
              onMouseDown={() => {
                setSearchTerm(category);
                handleSearch(category);
              }}
              className="px-2 py-1 border border-gray-300 rounded-full text-xs sm:text-sm hover:bg-black hover:text-white transition"
            >
              {category}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
</footer>

      </div>
    </div>
  );
}
